import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { Control } from 'mapbox-gl';
import { point, polygon } from '@turf/helpers';
import { booleanPointInPolygon } from '@turf/turf';
import { MapTypes } from '../../reducers/menuReducer';
import { MapStyleSettingsState, useMapStyleSettings } from '../../reducers/mapStyleSettingsReducer';
import {
  SOURCE_ID,
  SOURCE_ID_JAPAN,
  getMapboxStyle,
  updateStyle,
  waitForStyleLoaded,
} from './MapStyle/utils';
import empty from './styles/empty.json';
import 'mapbox-gl/dist/mapbox-gl.css';
import './GlMap.css';

// Add support for RTL text
if (mapboxgl.getRTLTextPluginStatus() === 'unavailable') {
  mapboxgl.setRTLTextPlugin(
    'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.0/mapbox-gl-rtl-text.js',
    () => {},
  );
}

type ControlLocation =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

interface MapProps {
  independent?: boolean;
  mapboxRef?: (c: mapboxgl.Map) => void;
  mapTypeName?: MapTypes;
  children?: React.ReactNode;
  controlLocation?: ControlLocation;
  showControls?: boolean;
  disableScroll?: boolean;
}

export const MapContext = React.createContext<mapboxgl.Map | null>(null);

const createMapInstance = () =>
  new mapboxgl.Map({
    container: document.createElement('div'),
    style: empty as mapboxgl.Style,
    center: [-9.445531804936309, 35.4606814723237],
    zoom: 1.5,
    minZoom: 1,
    maxZoom: 20,
    preserveDrawingBuffer: true,
  });

interface MapInfo {
  instance: mapboxgl.Map;
  controlLocation?: ControlLocation;
  control?: Control;
}
const globalMap: MapInfo = {
  instance: createMapInstance(),
};

const currentYear = new Date().getFullYear();
const GlMap: React.FC<MapProps> = ({
  children,
  disableScroll = false,
  mapboxRef,
  mapTypeName = 'Genesis',
  independent = false,
  controlLocation = 'top-left',
  showControls = true,
}) => {
  const [
    mapStyleSettings,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMapStyleSettings,
    { mapStyleSettingsReady },
  ] = useMapStyleSettings();
  const [map] = useState<MapInfo>(() =>
    independent
      ? { instance: createMapInstance(), currentStyle: undefined }
      : globalMap,
  );
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<any>();

  if (mapboxRef) {
    mapboxRef(map.instance);
  }

  const initJapanFilter = (e: mapboxgl.MapDataEvent & mapboxgl.EventData) => {
    if (
      e.dataType === 'source' &&
      e.sourceDataType === 'content' &&
      e.sourceId === SOURCE_ID_JAPAN
    ) {
      return;
    }
    const japanPolygon = polygon([
      [
        [123.82335072878072, 22.870897580462014],
        [122.78200591353999, 25.393899882518596],
        [127.34141836401284, 30.533053302288934],
        [127.42115532304064, 33.29237284171087],
        [128.19690217899915, 33.73046218111152],
        [130.3950945463655, 35.83820754777882],
        [137.20118251643692, 41.47464157220597],
        [141.06954532867246, 45.83404598993661],
        [151.52650238931017, 46.64250634326723],
        [142.89338881166321, 24.00474948920443],
        [123.82335072878072, 22.870897580462014],
      ],
    ]);

    function lngFromMercatorX(x: number) {
      return x * 360 - 180;
    }

    function latFromMercatorY(y: number) {
      const y2 = 180 - y * 360;
      return (360 / Math.PI) * Math.atan(Math.exp((y2 * Math.PI) / 180)) - 90;
    }

    function tileInJapan(tileId: any): boolean {
      const { x, y, z } = tileId.canonical;

      if (z < 7) return false;

      const worldSize = Math.pow(2, z);

      // Coordinate of the center of the tile
      const lng = lngFromMercatorX((x + 0.5) / worldSize);
      const lat = latFromMercatorY((y + 0.5) / worldSize);

      return booleanPointInPolygon(point([lng, lat]), japanPolygon);
    }

    function setTileFilter(sourceName: string, fn: (tile: any) => boolean) {
      const source = map.instance.getSource(sourceName);

      if (source) {
        (source as any).hasTile = fn;
      }
    }
    setTileFilter(SOURCE_ID_JAPAN, (tile) => tileInJapan(tile));
    setTileFilter(SOURCE_ID, (tile) => !tileInJapan(tile));
  };

  const updateControls = () => {
    if (map.control && map.instance.hasControl(map.control)) {
      map.instance.removeControl(map.control);
    }
    if (showControls) {
      map.control = new mapboxgl.NavigationControl();
      map.controlLocation = controlLocation;
      map.instance.addControl(map.control, controlLocation);
    }
  };

  useEffect(() => {
    if (!mapStyleSettingsReady) {
      return;
    }

    const create = async () => {
      setLoaded(false);
      // Mount map to the DOM element
      if (containerRef.current !== undefined) {
        containerRef.current.appendChild(map.instance.getContainer());
        window.dispatchEvent(new Event('resize'));
      }

      // Add controls
      if (controlLocation !== map.controlLocation) {
        updateControls();
      }

      // Set initial style
      const style = await getMapboxStyle(mapStyleSettings, mapTypeName);
      map.instance.setStyle(style);
      await waitForStyleLoaded(map.instance);
      setLoaded(true);
    };

    create();
  }, [mapStyleSettingsReady]);

  useEffect(() => {
    updateControls();
  }, [showControls, controlLocation]);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    setLoaded(false);
    updateStyle(map.instance, mapStyleSettings, mapTypeName).then(() =>
      setLoaded(true),
    );
  }, [
    // Listed separately as mapStyleSettings object reference might change on component (re) render which could trigger an unnecessary blink
    mapStyleSettings.basicPOI,
    mapStyleSettings.buildings3D,
    mapStyleSettings.languageGenesis,
    mapStyleSettings.languageOrbis,
    mapStyleSettings.latin,
    mapStyleSettings.style,
    mapTypeName,
  ]);

  // Init japan filtering after source was created but before map is loaded
  // to prevent blink after load
  // Only required for Genesis map, Orbis is detailed.
  useEffect(() => {
    if (mapTypeName === 'Genesis') {
      map.instance.on('data', initJapanFilter);

      return () => {
        map.instance.off('data', initJapanFilter);
      };
    }
  }, [mapTypeName]);

  useEffect(() => {
    if (disableScroll) {
      map.instance.scrollZoom.disable();
    } else {
      map.instance.scrollZoom.enable();
    }
  }, [disableScroll]);

  return (
    <div ref={containerRef} className="GlMap">
      {loaded ? (
        <MapContext.Provider value={map.instance}>
          {children}
        </MapContext.Provider>
      ) : null}
      <div className="GlMap__attribution">
        <a href="https://www.tomtom.com/en_gb/thirdpartyproductterms/ ">
          © TomTom 1992 – {currentYear}
        </a>
      </div>
    </div>
  );
};

export default GlMap;
